<template>
  <div class="cart-page">
    <div class="cart-container">

      <!-- Breadcrumb -->
      <nav class="breadcrumb-nav">
        <router-link to="/" class="breadcrumb-link"><i class="bi bi-house"></i></router-link>
        <i class="bi bi-chevron-right breadcrumb-sep"></i>
        <span class="breadcrumb-current">Winkelwagen</span>
      </nav>

      <!-- Progress indicator -->
      <div class="progress-bar">
        <div class="progress-step active">
          <div class="progress-dot active-dot"></div>
          <span class="progress-label active-label">Winkelwagen</span>
        </div>
        <div class="progress-line"></div>
        <div class="progress-step">
          <div class="progress-dot inactive-dot"></div>
          <span class="progress-label inactive-label">Bestellen</span>
        </div>
        <div class="progress-line"></div>
        <div class="progress-step">
          <div class="progress-dot inactive-dot"></div>
          <span class="progress-label inactive-label">Bevestiging</span>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p class="loading-text">Winkelwagen laden...</p>
      </div>

      <!-- Empty cart -->
      <div v-else-if="isEmpty" class="empty-cart">
        <div class="empty-icon-wrap">
          <i class="bi bi-cart empty-icon"></i>
        </div>
        <h2 class="empty-title">Je winkelwagen is leeg</h2>
        <p class="empty-subtitle">Ontdek ons assortiment en vind de juiste lader of zonnepanelen voor jouw situatie.</p>
        <router-link to="/producten" class="cta-button">
          <i class="bi bi-grid"></i>
          Bekijk alle producten
        </router-link>
        <div class="empty-trust-strip">
          <div class="trust-item">
            <i class="bi bi-shield-check trust-icon"></i>
            <span>2 jaar garantie</span>
          </div>
          <div class="trust-item">
            <i class="bi bi-truck trust-icon"></i>
            <span>Gratis verzending vanaf €50</span>
          </div>
          <div class="trust-item">
            <i class="bi bi-arrow-clockwise trust-icon"></i>
            <span>30 dagen retour</span>
          </div>
        </div>
      </div>

      <!-- Cart with items -->
      <div v-else class="cart-layout">

        <!-- Left: cart items -->
        <div class="cart-items-col">

          <!-- Header row -->
          <div class="cart-header">
            <h1 class="cart-title">
              Winkelwagen
              <span class="cart-count">({{ itemCount }} {{ itemCount === 1 ? 'product' : 'producten' }})</span>
            </h1>
            <button @click="confirmClearCart" class="clear-cart-link">
              Winkelwagen legen
            </button>
          </div>

          <!-- Free shipping progress -->
          <div v-if="!hasFreeShipping" class="shipping-progress-card">
            <div class="shipping-progress-label">
              <i class="bi bi-truck shipping-truck"></i>
              Nog <strong>{{ formatPrice(amountToFreeShipping) }}</strong> tot gratis verzending
            </div>
            <div class="shipping-bar-track">
              <div class="shipping-bar-fill" :style="{ width: `${shippingProgress}%` }"></div>
            </div>
          </div>
          <div v-else class="shipping-free-badge">
            <i class="bi bi-check-circle-fill"></i>
            <strong>Gratis verzending</strong>&nbsp;van toepassing op je bestelling.
          </div>

          <!-- Cart item cards -->
          <template v-for="group in groupedCartItems" :key="group.label || group.item?.product.id">

            <!-- Set group -->
            <div v-if="group.type === 'set'" class="item-card">
              <div class="set-header">
                <i class="bi bi-box-seam set-icon"></i>
                <span class="set-label">{{ group.label }}</span>
                <span class="set-count">{{ group.items.length }} producten</span>
              </div>
              <div
                v-for="(item, idx) in group.items"
                :key="item.product.id"
                :class="['item-row', idx < group.items.length - 1 ? 'item-row-border' : '']"
              >
                <router-link :to="`/product/${item.product.slug}`" class="item-image-link">
                  <img
                    :src="item.product.images?.[0]?.src || '/placeholder-product.jpg'"
                    :alt="item.product.name"
                    class="item-image"
                  >
                </router-link>
                <div class="item-details">
                  <div class="item-top-row">
                    <router-link :to="`/product/${item.product.slug}`" class="item-name-link">
                      <p class="item-name">{{ item.product.name }}</p>
                    </router-link>
                    <button @click="confirmRemove(item)" class="remove-btn" title="Verwijder">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                  <div class="item-bottom-row">
                    <div class="qty-control">
                      <button @click="decreaseQuantity(item)" :disabled="item.quantity <= 1 || updating" class="qty-btn"><i class="bi bi-dash"></i></button>
                      <input type="number" :value="item.quantity" @change="handleQuantityInput(item, $event)" min="1" :disabled="updating" class="qty-input">
                      <button @click="increaseQuantity(item)" :disabled="updating" class="qty-btn"><i class="bi bi-plus"></i></button>
                    </div>
                    <div class="item-price-block">
                      <div v-if="item.product.on_sale" class="item-price-old">{{ formatPrice(parseFloat(item.product.regular_price) * item.quantity) }}</div>
                      <div class="item-price">{{ formatPrice(parseFloat(item.product.price) * item.quantity) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Single item -->
            <div v-else class="item-card item-card-single">
              <router-link :to="`/product/${group.item.product.slug}`" class="item-image-link">
                <div class="item-image-wrap">
                  <img
                    :src="group.item.product.images?.[0]?.src || '/placeholder-product.jpg'"
                    :alt="group.item.product.name"
                    class="item-image"
                  >
                  <span v-if="group.item.product.on_sale" class="sale-badge">SALE</span>
                </div>
              </router-link>
              <div class="item-details">
                <div class="item-top-row">
                  <router-link :to="`/product/${group.item.product.slug}`" class="item-name-link">
                    <h3 class="item-name">{{ group.item.product.name }}</h3>
                  </router-link>
                  <button @click="confirmRemove(group.item)" class="remove-btn" title="Verwijder">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
                <div class="item-meta">
                  <span v-if="group.item.product.sku" class="item-sku">SKU: {{ group.item.product.sku }}</span>
                  <span v-if="group.item.product.in_stock" class="item-stock">
                    <i class="bi bi-check-circle"></i> Op voorraad
                  </span>
                </div>
                <div class="item-bottom-row">
                  <div class="qty-control">
                    <button @click="decreaseQuantity(group.item)" :disabled="group.item.quantity <= 1 || updating" class="qty-btn"><i class="bi bi-dash"></i></button>
                    <input type="number" :value="group.item.quantity" @change="handleQuantityInput(group.item, $event)" min="1" :max="group.item.product.stock_quantity || 999" :disabled="updating" class="qty-input">
                    <button @click="increaseQuantity(group.item)" :disabled="updating" class="qty-btn"><i class="bi bi-plus"></i></button>
                  </div>
                  <div class="item-price-block">
                    <div v-if="group.item.product.on_sale" class="item-price-old">{{ formatPrice(parseFloat(group.item.product.regular_price) * group.item.quantity) }}</div>
                    <div class="item-price">{{ formatPrice(parseFloat(group.item.product.price) * group.item.quantity) }}</div>
                    <div class="item-price-unit">{{ formatPrice(group.item.product.price) }} / st</div>
                  </div>
                </div>
              </div>
            </div>

          </template>

          <!-- Coupon code -->
          <div class="coupon-card">
            <button @click="showCouponInput = !showCouponInput" class="coupon-toggle">
              <i class="bi bi-tag coupon-icon"></i>
              <span class="coupon-toggle-label">Kortingscode toevoegen</span>
              <i :class="['bi coupon-chevron', showCouponInput ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
            </button>

            <transition name="slide-down">
              <div v-if="showCouponInput" class="coupon-body">
                <div class="coupon-input-row">
                  <input
                    v-model="couponCode"
                    type="text"
                    placeholder="Voer je kortingscode in"
                    @keyup.enter="applyCoupon"
                    :class="['coupon-input', couponError ? 'coupon-input-error' : '']"
                  >
                  <button @click="applyCoupon" :disabled="!couponCode || applyingCoupon" class="coupon-apply-btn">
                    {{ applyingCoupon ? 'Laden...' : 'Toepassen' }}
                  </button>
                </div>
                <p v-if="couponError" class="coupon-msg coupon-msg-error">
                  <i class="bi bi-exclamation-circle"></i> {{ couponError }}
                </p>
                <p v-if="couponSuccess" class="coupon-msg coupon-msg-success">
                  <i class="bi bi-check-circle"></i> {{ couponSuccess }}
                </p>
              </div>
            </transition>

            <div v-if="appliedCoupons.length > 0" class="applied-coupons">
              <div v-for="coupon in appliedCoupons" :key="coupon.code" class="coupon-tag">
                <i class="bi bi-tag"></i>
                {{ coupon.code }}
                <span>-{{ formatPrice(coupon.discount) }}</span>
                <button @click="removeCoupon(coupon.code)" class="coupon-remove-btn">
                  <i class="bi bi-x"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Continue shopping link -->
          <div class="continue-shopping">
            <router-link to="/producten" class="continue-link">
              <i class="bi bi-arrow-left"></i>
              Verder winkelen
            </router-link>
          </div>
        </div>

        <!-- Right: order summary -->
        <div class="summary-col">
          <div class="summary-card">

            <div class="summary-body">
              <h3 class="summary-title">Besteloverzicht</h3>

              <div class="summary-rows">
                <div class="summary-row">
                  <span class="summary-label">Subtotaal</span>
                  <span class="summary-value">{{ formatPrice(totals.subtotal) }}</span>
                </div>
                <div v-if="totals.discount > 0" class="summary-row">
                  <span class="summary-label summary-discount-label">
                    <i class="bi bi-tag"></i> Korting
                  </span>
                  <span class="summary-value summary-discount-value">-{{ formatPrice(totals.discount) }}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">
                    <i class="bi bi-truck"></i> Verzendkosten
                  </span>
                  <span v-if="hasFreeShipping" class="summary-value summary-free-value">Gratis</span>
                  <span v-else class="summary-value">{{ formatPrice(shippingCost) }}</span>
                </div>
                <div class="summary-row summary-row-tax">
                  <span class="summary-label-muted">Waarvan BTW (21%)</span>
                  <span class="summary-value-muted">{{ formatPrice(totals.tax) }}</span>
                </div>
              </div>

              <div class="summary-total-row">
                <span class="summary-total-label">Totaal</span>
                <div class="summary-total-right">
                  <div class="summary-total-amount">{{ formatPrice(grandTotal) }}</div>
                  <div class="summary-total-vat">Incl. BTW</div>
                </div>
              </div>

              <router-link to="/checkout" class="checkout-btn">
                <i class="bi bi-lock-fill"></i>
                Veilig afrekenen
              </router-link>

              <!-- Trust strip -->
              <div class="trust-strip">
                <div class="trust-strip-item">
                  <i class="bi bi-shield-check trust-strip-icon"></i>
                  <span class="trust-strip-label">Veilig betalen</span>
                </div>
                <div class="trust-strip-item">
                  <i class="bi bi-truck trust-strip-icon"></i>
                  <span class="trust-strip-label">Morgen in huis</span>
                </div>
                <div class="trust-strip-item">
                  <i class="bi bi-arrow-clockwise trust-strip-icon"></i>
                  <span class="trust-strip-label">30 dagen retour</span>
                </div>
              </div>

              <!-- Payment methods -->
              <div class="payment-methods">
                <span class="payment-methods-label">Betaal met:</span>
                <img
                  v-for="pm in paymentMethods"
                  :key="pm.key"
                  :src="`/img/payment/${pm.key}.svg`"
                  :alt="pm.label"
                  class="payment-icon"
                  :style="{ minWidth: pm.wide ? '44px' : '32px' }"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          <!-- Help card -->
          <div class="help-card">
            <div class="help-icon-wrap">
              <i class="bi bi-headset help-icon"></i>
            </div>
            <div class="help-content">
              <p class="help-title">Hulp nodig?</p>
              <p class="help-sub">Onze experts helpen je graag</p>
              <a href="mailto:info@plug2charge.nl" class="help-phone">
                <i class="bi bi-envelope"></i>
                Stuur een e‑mail
              </a>
              <a href="/klantenservice#livechat" class="help-phone" style="margin-top:.35rem">
                <i class="bi bi-chat-dots"></i>
                Live chat
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Remove item confirmation modal -->
    <transition name="fade">
      <div v-if="itemToRemove" class="modal-overlay" @click="cancelRemove">
        <div class="modal-box" @click.stop>
          <div class="modal-header">
            <h4 class="modal-title">Product verwijderen?</h4>
            <button @click="cancelRemove" class="modal-close"><i class="bi bi-x"></i></button>
          </div>
          <div class="modal-body">
            <p class="modal-text">
              Weet je zeker dat je <strong>{{ itemToRemove.product?.name }}</strong> uit je winkelwagen wilt verwijderen?
            </p>
          </div>
          <div class="modal-footer">
            <button @click="cancelRemove" class="modal-btn-cancel">Annuleren</button>
            <button @click="removeItemConfirmed" class="modal-btn-confirm">
              <i class="bi bi-trash"></i> Verwijderen
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Clear cart confirmation modal -->
    <transition name="fade">
      <div v-if="showClearConfirm" class="modal-overlay" @click="cancelClearCart">
        <div class="modal-box" @click.stop>
          <div class="modal-header">
            <h4 class="modal-title">Winkelwagen legen?</h4>
            <button @click="cancelClearCart" class="modal-close"><i class="bi bi-x"></i></button>
          </div>
          <div class="modal-body">
            <p class="modal-text">Weet je zeker dat je alle producten uit je winkelwagen wilt verwijderen?</p>
          </div>
          <div class="modal-footer">
            <button @click="cancelClearCart" class="modal-btn-cancel">Annuleren</button>
            <button @click="clearCartConfirmed" class="modal-btn-confirm">
              <i class="bi bi-trash"></i> Alles verwijderen
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useCartStore } from '@stores/cart'
import { useAnalytics } from '@composables/useAnalytics'

const cartStore = useCartStore()
const { trackViewCart, trackRemoveFromCart, trackBeginCheckout } = useAnalytics()

// State
const loading         = ref(false)
const updating        = ref(false)
const showCouponInput = ref(false)
const couponCode      = ref('')
const applyingCoupon  = ref(false)
const couponError     = ref('')
const couponSuccess   = ref('')
const itemToRemove    = ref(null)
const showClearConfirm = ref(false)

// Constants
const FREE_SHIPPING_THRESHOLD = 50
const SHIPPING_COST = 6.95

const paymentMethods = [
  { key: 'ideal',      label: 'iDEAL',     wide: false },
  { key: 'visa',       label: 'Visa',       wide: false },
  { key: 'mastercard', label: 'Mastercard', wide: false },
  { key: 'paypal',     label: 'PayPal',     wide: true  },
  { key: 'bancontact', label: 'Bancontact', wide: false },
  { key: 'klarna',     label: 'Klarna',     wide: true  },
]

const trustUsps = [
  { icon: 'bi-truck',          text: 'Gratis verzending vanaf €50' },
  { icon: 'bi-shield-check',   text: '2 jaar garantie' },
  { icon: 'bi-arrow-clockwise', text: '30 dagen retour' },
]

const trustBadges = [
  { icon: 'bi-shield-check',    text: 'Veilig betalen' },
  { icon: 'bi-truck',           text: 'Morgen in huis' },
  { icon: 'bi-arrow-clockwise', text: '30 dagen retour' },
]

// Computed
const items    = computed(() => cartStore.items)

const groupedCartItems = computed(() => {
  const groups = []
  const seen = new Set()
  for (const item of items.value) {
    if (item.setLabel) {
      if (!seen.has(item.setLabel)) {
        seen.add(item.setLabel)
        groups.push({ type: 'set', label: item.setLabel, items: items.value.filter(i => i.setLabel === item.setLabel) })
      }
    } else {
      groups.push({ type: 'single', item })
    }
  }
  return groups
})
const isEmpty  = computed(() => cartStore.items.length === 0)
const subtotal = computed(() => cartStore.subtotal || 0)
const itemCount = computed(() => cartStore.itemCount)

const totals = computed(() => ({
  subtotal: cartStore.subtotal || 0,
  discount: cartStore.discountAmount || 0,
  tax: (cartStore.subtotal || 0) * 0.21 / 1.21,
}))

const appliedCoupons = computed(() => {
  if (!cartStore.appliedCoupon) return []
  return [{
    code: cartStore.appliedCoupon.code,
    discount: cartStore.discountAmount
  }]
})

const hasFreeShipping      = computed(() => subtotal.value >= FREE_SHIPPING_THRESHOLD)
const amountToFreeShipping = computed(() => Math.max(FREE_SHIPPING_THRESHOLD - subtotal.value, 0))
const shippingProgress     = computed(() => Math.min((subtotal.value / FREE_SHIPPING_THRESHOLD) * 100, 100))
const shippingCost         = computed(() => hasFreeShipping.value ? 0 : SHIPPING_COST)

const grandTotal = computed(() => {
  const sub      = parseFloat(totals.value?.subtotal || 0)
  const discount = parseFloat(totals.value?.discount || 0)
  return sub - discount + shippingCost.value
})

// Methods
import { formatPrice } from '@/utils/price'

const decreaseQuantity = async (item) => {
  if (item.quantity <= 1) return
  updating.value = true
  cartStore.updateQuantity(item.product.id, item.quantity - 1)
  updating.value = false
}

const increaseQuantity = async (item) => {
  updating.value = true
  cartStore.updateQuantity(item.product.id, item.quantity + 1)
  updating.value = false
}

const handleQuantityInput = async (item, event) => {
  const newQty = parseInt(event.target.value)
  if (isNaN(newQty) || newQty < 1) { event.target.value = item.quantity; return }
  updating.value = true
  cartStore.updateQuantity(item.product.id, newQty)
  updating.value = false
}

const confirmRemove       = (item) => { itemToRemove.value = item }
const cancelRemove        = () => { itemToRemove.value = null }
const removeItemConfirmed = () => {
  if (!itemToRemove.value) return
  trackRemoveFromCart(itemToRemove.value.product, itemToRemove.value.quantity)
  cartStore.removeFromCart(itemToRemove.value.product.id)
  itemToRemove.value = null
}

const confirmClearCart  = () => { showClearConfirm.value = true }
const cancelClearCart   = () => { showClearConfirm.value = false }
const clearCartConfirmed = () => {
  cartStore.clearCart?.()
  showClearConfirm.value = false
}

onMounted(() => {
  trackViewCart(cartStore.items, cartStore.subtotal)
})

const applyCoupon = async () => {
  if (!couponCode.value) return
  applyingCoupon.value = true
  couponError.value    = ''
  couponSuccess.value  = ''
  try {
    await cartStore.applyCoupon(couponCode.value.trim())
    couponSuccess.value = `Kortingscode "${couponCode.value.trim().toUpperCase()}" is toegepast.`
    couponCode.value = ''
    setTimeout(() => { couponSuccess.value = '' }, 4000)
  } catch (e) {
    couponError.value = e.message || 'Ongeldige kortingscode'
  } finally {
    applyingCoupon.value = false
  }
}

const removeCoupon = () => {
  cartStore.removeCoupon()
  couponError.value   = ''
  couponSuccess.value = ''
}
</script>

<style scoped>
/* ─── Page shell ─────────────────────────────────────────────── */
.cart-page {
  background: #f6f7f9;
  min-height: 100dvh;
  padding: 2.5rem 0 4rem;
}

.cart-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

/* ─── Breadcrumb ─────────────────────────────────────────────── */
.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #999;
  margin-bottom: 1.5rem;
}
.breadcrumb-link {
  color: #999;
  text-decoration: none;
  transition: color 0.15s;
}
.breadcrumb-link:hover { color: #111; }
.breadcrumb-sep { font-size: 0.65rem; }
.breadcrumb-current { color: #111; font-weight: 500; }

/* ─── Progress indicator ─────────────────────────────────────── */
.progress-bar {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 2.5rem;
}
.progress-step {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.progress-line {
  flex: 0 0 48px;
  height: 1px;
  background: #ddd;
  margin: 0 0.5rem;
}
.progress-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.active-dot {
  background: #FFD500;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.25);
}
.inactive-dot {
  background: #ddd;
}
.progress-label {
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
}
.active-label  { color: #111; font-weight: 700; }
.inactive-label { color: #aaa; }

/* ─── Loading ────────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8rem 0;
}
.loading-spinner {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid #f0f0f0;
  border-top-color: #FFD500;
  animation: spin 0.7s linear infinite;
  margin-bottom: 1rem;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-text { color: #888; font-size: 0.9rem; }

/* ─── Empty cart ─────────────────────────────────────────────── */
.empty-cart {
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
  padding: 4rem 0 6rem;
}
.empty-icon-wrap {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #e9eaec;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.75rem;
}
.empty-icon {
  font-size: 2.5rem;
  color: #FFD500;
}
.empty-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;
}
.empty-subtitle {
  font-size: 0.95rem;
  color: #888;
  line-height: 1.6;
  margin: 0 0 2rem;
  text-wrap: pretty;
}
.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #FFD500;
  color: #111;
  font-weight: 700;
  font-size: 0.9375rem;
  padding: 0.875rem 1.75rem;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.18s, transform 0.15s;
}
.cta-button:hover {
  background: #ffb800;
  transform: translateY(-1px);
}
.empty-trust-strip {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
}
.trust-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #888;
}
.trust-icon { color: #FFD500; font-size: 0.9rem; }

/* ─── Two-column layout ──────────────────────────────────────── */
.cart-layout {
  display: flex;
  gap: 1.75rem;
  align-items: flex-start;
}
.cart-items-col {
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.summary-col {
  flex: 1;
  min-width: 300px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1.5rem;
}

/* ─── Cart header ────────────────────────────────────────────── */
.cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}
.cart-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #111;
  margin: 0;
  letter-spacing: -0.02em;
}
.cart-count {
  font-size: 1rem;
  font-weight: 400;
  color: #888;
  margin-left: 0.35rem;
}
.clear-cart-link {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: #aaa;
  text-decoration: none;
  padding: 0;
  transition: color 0.15s;
}
.clear-cart-link:hover { color: #555; }

/* ─── Shipping progress ──────────────────────────────────────── */
.shipping-progress-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.shipping-progress-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.75rem;
}
.shipping-truck { color: #FFD500; }
.shipping-bar-track {
  width: 100%;
  height: 6px;
  background: #f0f0f0;
  border-radius: 99px;
  overflow: hidden;
}
.shipping-bar-fill {
  height: 100%;
  background: #FFD500;
  border-radius: 99px;
  transition: width 0.5s ease;
}
.shipping-free-badge {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 0.875rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.875rem;
  color: #15803d;
  font-weight: 500;
}

/* ─── Item cards ─────────────────────────────────────────────── */
.item-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
}
.item-card-single {
  display: flex;
  gap: 1rem;
  padding: 1.125rem;
  transition: box-shadow 0.2s;
}
.item-card-single:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }

/* Set group */
.set-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #f0f0f0;
}
.set-icon   { color: #FFD500; font-size: 0.8rem; }
.set-label  { font-size: 0.8rem; font-weight: 700; color: #111; flex: 1; }
.set-count  { font-size: 0.7rem; color: #aaa; }

.item-row {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  transition: background 0.15s;
}
.item-row:hover { background: #fafafa; }
.item-row-border { border-bottom: 1px solid #f5f5f5; }

/* Product image */
.item-image-link { flex-shrink: 0; }
.item-image-wrap {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}
.item-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #fff;
  display: block;
}
.sale-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  background: #ef4444;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 4px;
}

/* Item details */
.item-details {
  flex: 1;
  min-width: 0;
}
.item-top-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.item-name-link { text-decoration: none; }
.item-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
  line-height: 1.35;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s;
}
.item-name-link:hover .item-name { color: #b89b00; }

.remove-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  border-radius: 6px;
  font-size: 0.8rem;
  transition: color 0.15s, background 0.15s;
}
.remove-btn:hover { color: #ef4444; background: #fef2f2; }

.item-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.item-sku   { font-size: 0.7rem; color: #aaa; }
.item-stock { font-size: 0.7rem; color: #16a34a; font-weight: 500; }

.item-bottom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Quantity control */
.qty-control {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}
.qty-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: #555;
  font-size: 0.75rem;
  transition: background 0.15s;
}
.qty-btn:hover:not(:disabled) { background: #f5f5f5; }
.qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.qty-input {
  width: 40px;
  height: 34px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #111;
  border: none;
  border-left: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  outline: none;
  background: #fff;
}
.qty-input:disabled { opacity: 0.6; }

/* Prices */
.item-price-block { text-align: right; }
.item-price-old   { font-size: 0.75rem; color: #bbb; text-decoration: line-through; }
.item-price       { font-size: 1.125rem; font-weight: 800; color: #111; font-variant-numeric: tabular-nums; }
.item-price-unit  { font-size: 0.7rem; color: #aaa; }

/* ─── Coupon card ────────────────────────────────────────────── */
.coupon-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
}
.coupon-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #444;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
}
.coupon-toggle:hover { background: #fafafa; }
.coupon-icon    { color: #FFD500; }
.coupon-toggle-label { flex: 1; }
.coupon-chevron { font-size: 0.7rem; color: #aaa; }

.coupon-body {
  border-top: 1px solid #f0f0f0;
  padding: 1rem 1.25rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.coupon-input-row {
  display: flex;
  gap: 0.625rem;
}
.coupon-input {
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.coupon-input:focus {
  border-color: #FFD500;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.15);
}
.coupon-input-error { border-color: #ef4444; }
.coupon-input-error:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1); }

.coupon-apply-btn {
  padding: 0.625rem 1.125rem;
  background: #111;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.coupon-apply-btn:hover:not(:disabled) { background: #333; }
.coupon-apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.coupon-msg {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  margin: 0;
}
.coupon-msg-error   { color: #ef4444; }
.coupon-msg-success { color: #16a34a; }

.applied-coupons {
  border-top: 1px solid #f0f0f0;
  padding: 0.75rem 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.coupon-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 99px;
}
.coupon-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}
.coupon-remove-btn:hover { color: #ef4444; }

/* ─── Continue shopping ──────────────────────────────────────── */
.continue-shopping { padding-top: 0.25rem; }
.continue-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: #777;
  text-decoration: none;
  transition: color 0.15s;
}
.continue-link:hover { color: #111; }

/* ─── Summary card ───────────────────────────────────────────── */
.summary-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #e9eaec;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
}
.summary-body { padding: 1.5rem; }
.summary-title {
  font-size: 1rem;
  font-weight: 800;
  color: #111;
  margin: 0 0 1.25rem;
  letter-spacing: -0.01em;
}

.summary-rows { display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.25rem; }
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}
.summary-row-tax { margin-top: -0.25rem; }
.summary-label         { color: #666; }
.summary-label-muted   { color: #aaa; font-size: 0.85rem; }
.summary-value         { font-weight: 500; color: #111; font-variant-numeric: tabular-nums; }
.summary-value-muted   { color: #aaa; font-size: 0.85rem; font-variant-numeric: tabular-nums; }
.summary-discount-label { color: #16a34a; display: flex; align-items: center; gap: 0.3rem; }
.summary-discount-value { font-weight: 600; color: #16a34a; }
.summary-free-value     { font-weight: 600; color: #16a34a; }

.summary-total-row {
  border-top: 1px solid #f0f0f0;
  padding-top: 1rem;
  margin-bottom: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.summary-total-label  { font-weight: 700; font-size: 1rem; color: #111; }
.summary-total-right  { text-align: right; }
.summary-total-amount { font-size: 1.625rem; font-weight: 800; color: #111; letter-spacing: -0.03em; font-variant-numeric: tabular-nums; }
.summary-total-vat    { font-size: 0.7rem; color: #aaa; margin-top: 1px; }

/* ─── Checkout button ────────────────────────────────────────── */
.checkout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  background: #FFD500;
  color: #111;
  font-weight: 700;
  font-size: 0.9375rem;
  padding: 0.9rem 1.5rem;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.18s, transform 0.15s, box-shadow 0.18s;
  box-sizing: border-box;
}
.checkout-btn:hover {
  background: #ffb800;
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(255, 213, 0, 0.4);
}
.checkout-btn:active {
  transform: translateY(0) scale(0.99);
}
.checkout-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.45);
}

/* ─── Trust strip ────────────────────────────────────────────── */
.trust-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}
.trust-strip-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.3rem;
}
.trust-strip-icon  { color: #FFD500; font-size: 1.1rem; }
.trust-strip-label { font-size: 0.68rem; color: #888; line-height: 1.3; }

/* ─── Payment methods ────────────────────────────────────────── */
.payment-methods {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}
.payment-methods-label { font-size: 0.7rem; color: #aaa; font-weight: 500; white-space: nowrap; }
.payment-icon {
  height: 22px;
  width: auto;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  padding: 2px 4px;
  box-sizing: content-box;
}

/* ─── Help card ──────────────────────────────────────────────── */
.help-card {
  background: #fff;
  border: 1px solid #e9eaec;
  border-radius: 12px;
  padding: 1.125rem;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.help-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid rgba(255, 213, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.help-icon    { font-size: 1.125rem; color: #FFD500; }
.help-title   { font-weight: 700; font-size: 0.875rem; color: #111; margin: 0 0 2px; }
.help-sub     { font-size: 0.78rem; color: #888; margin: 0 0 0.5rem; }
.help-phone {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #FFD500;
  text-decoration: none;
  transition: color 0.15s;
}
.help-phone:hover { color: #ffb800; }

/* ─── Modal ──────────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.48);
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
  max-width: 400px;
  width: 100%;
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}
.modal-title { font-weight: 700; font-size: 1rem; color: #111; margin: 0; }
.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #ccc;
  font-size: 1.1rem;
  transition: color 0.15s;
  line-height: 1;
}
.modal-close:hover { color: #666; }
.modal-body  { padding: 1.25rem 1.5rem; }
.modal-text  { font-size: 0.9rem; color: #555; margin: 0; line-height: 1.55; }
.modal-footer {
  display: flex;
  gap: 0.75rem;
  padding: 0 1.5rem 1.25rem;
}
.modal-btn-cancel {
  flex: 1;
  padding: 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
  background: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.modal-btn-cancel:hover { border-color: #bbb; }
.modal-btn-confirm {
  flex: 1;
  padding: 0.625rem;
  background: #ef4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: background 0.15s;
}
.modal-btn-confirm:hover { background: #dc2626; }

/* ─── Transitions ────────────────────────────────────────────── */
.slide-down-enter-active,
.slide-down-leave-active { transition: all 0.2s ease; }
.slide-down-enter-from,
.slide-down-leave-to { opacity: 0; transform: translateY(-6px); }

.fade-enter-active,
.fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

/* ─── Hide number input spinners ─────────────────────────────── */
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }

/* ─── Responsive ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .cart-page { padding: 1.5rem 0 3rem; }
  .cart-container { padding: 0 1rem; }

  .progress-line { flex: 0 0 28px; margin: 0 0.25rem; }
  .progress-label { font-size: 0.75rem; }

  .cart-layout {
    flex-direction: column;
  }
  .summary-col {
    max-width: 100%;
    min-width: 0;
    width: 100%;
    position: static;
  }

  .item-card-single {
    flex-direction: row;
    gap: 0.875rem;
    padding: 1rem;
  }
  .item-image,
  .item-image-wrap {
    width: 72px;
    height: 72px;
  }

  .item-bottom-row {
    gap: 0.5rem;
  }
}
</style>
